/* eslint-disable jsdoc/require-jsdoc */
import type { Agent } from '@studio/models/agent';
import type { Organization } from '@studio/models/organization';
import type { RoomRecord } from '@studio/models/room';
import { AgentRegistryMembershipRole } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { StudioBadge, StudioDescriptor, Hierarchy } from './types.js';
import { Vendor, getCurrentVendor } from '@studio/design-system/themes/vendor.js';

const roleBadgeMap: Partial<Record<AgentRegistryMembershipRole, StudioBadge>> = {
  [AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_ADMIN]: {
    id: 'role-admin',
    label: 'ADMIN',
    tone: 'info'
  },
  [AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER]: {
    id: 'role-owner',
    label: 'OWNER',
    tone: 'neutral'
  },
  [AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_PRODUCER]: {
    id: 'role-producer',
    label: 'PRODUCER',
    tone: 'neutral'
  },
  [AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_VIEWER]: {
    id: 'role-viewer',
    label: 'GUEST',
    tone: 'neutral'
  },
  [AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_UNSPECIFIED]: {
    id: 'role-guest',
    label: 'GUEST',
    tone: 'neutral'
  }
};

const resolveStatus = (
  agentIds: Agent['id'][],
  agents: Record<Agent['id'], Agent>
): 'online' | 'offline' => {
  if (agentIds.length === 0) {
    return 'offline';
  }

  const isOnline = agentIds.some((id) => {
    const status = agents[id]?.status;

    return status !== undefined && status !== 'offline' && status !== 'offline-embedded';
  });

  return isOnline ? 'online' : 'offline';
};

const hasMachineAgent = (room: RoomRecord, machineAgentId: T_Maybe<Agent['id']>): boolean => {
  if (!machineAgentId) {
    return false;
  }

  const normalizedMachineAgentId = machineAgentId.toLowerCase();

  return Object.keys(room.agents).some(
    (agentId) => agentId.toLowerCase() === normalizedMachineAgentId
  );
};

// Role chips for the popped card (ENG-4037): unlike `buildBadges`, roles are
// NOT suppressed on this-device studios — the card shows both independently.
// Exported for the Studio picker, which also describes rooms whose org metadata is still loading.
export const buildRoles = (room: RoomRecord): StudioBadge[] => {
  const role = room.permissions?.role;

  if (role === undefined) {
    return [];
  }

  const roleBadge = roleBadgeMap[role];

  return roleBadge ? [roleBadge] : [];
};

const buildBadges = (room: RoomRecord, machineAgentId: T_Maybe<Agent['id']>): StudioBadge[] => {
  const badges: StudioBadge[] = [];

  if (hasMachineAgent(room, machineAgentId)) {
    badges.push({ id: 'this-device', label: 'THIS DEVICE', tone: 'success' });
  } else {
    const role = room.permissions?.role;

    if (role) {
      const roleBadge = roleBadgeMap[role];

      if (roleBadge) {
        badges.push(roleBadge);
      }
    }
  }

  return badges;
};

export const toData = (
  rooms: Record<RoomRecord['id'], RoomRecord>,
  orgs: Record<Organization['id'], Organization>,
  agents: Record<Agent['id'], Agent>,
  machineAgentId: T_Maybe<Agent['id']>
): StudioDescriptor[] | Hierarchy => {
  const isIris = getCurrentVendor() === Vendor.IRIS;

  if (isIris) {
    // New Iris format: flat list of StudioDescriptors
    return Object.values(rooms)
      .map((room) => {
        const organization = orgs[room.organizationId];

        if (!organization) {
          return undefined;
        }

        const agentIds = Object.keys(room.agents) as Agent['id'][];

        return {
          badges: buildBadges(room, machineAgentId),
          organization,
          room,
          status: resolveStatus(agentIds, agents),
          // ENG-4037 enrichment (spec §4.1) — all derived from depot data.
          // cameraCount stays undefined here: cameras are not part of the
          // registry snapshot toData receives; the card omits the chip.
          deviceCount: agentIds.length,
          isThisDevice: hasMachineAgent(room, machineAgentId),
          roles: buildRoles(room)
        } satisfies StudioDescriptor;
      })
      .filter((descriptor): descriptor is StudioDescriptor => descriptor !== undefined)
      .sort((a, b) => {
        // Check if studio is "THIS DEVICE"
        const aIsThisDevice = hasMachineAgent(a.room, machineAgentId);
        const bIsThisDevice = hasMachineAgent(b.room, machineAgentId);

        // Check if studio is OWNER
        const aIsOwner =
          a.room.permissions?.role ===
          AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER;
        const bIsOwner =
          b.room.permissions?.role ===
          AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER;

        // Priority 1: THIS DEVICE comes first
        if (aIsThisDevice !== bIsThisDevice) {
          return aIsThisDevice ? -1 : 1;
        }

        // Priority 2: OWNER comes before non-OWNER (owned studios before guest studios)
        if (aIsOwner !== bIsOwner) {
          return aIsOwner ? -1 : 1;
        }

        // Priority 3: Sort by creation date (newest first), fallback to alphabetical
        const aCreatedAt = a.room.createdAt;
        const bCreatedAt = b.room.createdAt;

        if (aCreatedAt && bCreatedAt) {
          // Both have creation dates - sort descending (newest first)
          return bCreatedAt - aCreatedAt;
        } else if (aCreatedAt && !bCreatedAt) {
          // Only a has creation date - a comes first
          return -1;
        } else if (!aCreatedAt && bCreatedAt) {
          // Only b has creation date - b comes first
          return 1;
        } else {
          // Neither has creation date - fallback to alphabetical
          return a.room.name.localeCompare(b.room.name, undefined, { sensitivity: 'base' });
        }
      });
  } else {
    // Old Hive/PTZOptics format: hierarchical structure
    return Object.values(orgs).map((org) => ({
      organization: org,
      rooms: Object.values(rooms).filter((r) => r.organizationId === org.id)
    }));
  }
};
